import { translateBackend } from '@assets/i18n/config';
import type { RelatedEntity, User } from '@interfaces/user';
import { EntityTypes, RelatedEntityTypes, UserStates } from '@interfaces/user';

import type { SxProps } from '@mui/material';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Dialog } from '@ovComponents/2-component/dialog/dialog';
import { Form } from '@ovComponents/2-component/form/form';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { RelatedEntitySelect } from '@ovComponents/3-pattern/relatedEntitySelect/relatedEntitySelect';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { legacyColors } from '@ovComponents/legacyColor';
import { RelatedEntityRemoveDialog } from '@pages/client/components/relatedEntityRemoveDialog';
import { UserContext } from '@providers/userContextProvider';
import { generateClientNameString } from '@utils/generateClientNameString';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from 'hephaestus/web';
import {
  AddRounded as AddRoundedIcon,
  CloseRounded as CloseRoundedIcon,
  Delete as DeleteIcon,
  Groups as GroupsIcon,
} from 'hephaestus/webIcons';
import { first, uniq } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const tableHeadSx = {
  textTransform: 'uppercase' as const,
  color: legacyColors.neutral600,
};
const tableRowSx: SxProps = {
  border: 'none',
  py: 2,
};

export const RelatedEntitiesVisual = ({
  user,
  options,
  loading,
  onDraftSubmission,
  onContinue,
}: {
  user?: User;
  options: any;
  loading: boolean;
  onDraftSubmission?: (relatedEntity: RelatedEntity) => void;
  onContinue: () => void;
}) => {
  const { t } = useTranslation([
    'client',
    'relatedEntityTypes',
    'workflowCompletions',
  ]);
  const [draftRelatedEntity, setDraftRelatedEntity] =
    useState<Partial<RelatedEntity>>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deletionCandidate, setDeletionCandidate] =
    useState<null | RelatedEntity>(null);

  const { userContext } = useContext(UserContext);

  const relatedEntities: RelatedEntity[] = user?.relatedEntities ?? [];
  useEffect(() => {
    if (
      user &&
      user.type !== EntityTypes.INDIVIDUAL &&
      options.enableAutoAssignRelatedEntities
    ) {
      const contextIndividuals =
        (userContext?.entities ?? []).filter(
          (e) => e.entity.type === EntityTypes.INDIVIDUAL,
        ) ?? [];
      if (
        uniq(contextIndividuals.map((entityObj: any) => entityObj?.entity?.id))
          .length === 1
      ) {
        /* TODO: should be contextIndividuals.length === 1, but currently we are experiencing a known
         bug which is entities getting duplicated after newly added
        */
        const relatedEntity = first(contextIndividuals)?.entity as User;
        const relation = RelatedEntityTypes.AuthorizedIndividual;
        const currentEntity = relatedEntities.find(
          (person: RelatedEntity) => person.entity.id === relatedEntity.id,
        );
        if (!currentEntity) {
          relatedEntities.push({ entity: relatedEntity, relation });
          setDraftRelatedEntity({
            ...draftRelatedEntity,
            entity: relatedEntity,
          });
          setDraftRelatedEntity({ ...draftRelatedEntity, relation });
          onDraftSubmission?.(draftRelatedEntity as RelatedEntity);
        }
      }
    }
  }, [user, userContext]);

  const isSubmissionDisabled = !(
    draftRelatedEntity?.entity && draftRelatedEntity?.relation
  );

  const handleStartAddRelation = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const getHandleDeleteFn = (relatedEntity: RelatedEntity) => {
    const handlerFn = () => {
      setDeletionCandidate(relatedEntity);
      setDeleteConfirmationOpen(true);
    };
    return handlerFn;
  };

  const handleSubmit = () => {
    if (isSubmissionDisabled) return;
    onDraftSubmission?.(draftRelatedEntity as RelatedEntity);
    setIsModalOpen(false);
  };

  const AddRelationButton = () => (
    <Button
      variant='outlined'
      startIcon={<AddRoundedIcon />}
      onClick={handleStartAddRelation}
      type='button'
    >
      {t('workflowCompletions:relatedEntities.addRelation')}
    </Button>
  );

  const LoadingAnimation = () => (
    <Box
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <CircularProgress />
    </Box>
  );

  const NoRelatedEntities = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Icon
        icon={GroupsIcon}
        size='150px'
        sx={{ color: legacyColors.neutral500 }}
      />

      <Typography variant='bodyLarge' sx={{ mt: -4 }}>
        {t('workflowCompletions:relatedEntities.noRelations')}
      </Typography>
      {!options.disableAdditionalRelationships && <AddRelationButton />}
    </Box>
  );

  const RelatedEntitiesTable = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography variant='labelSmall' sx={tableHeadSx}>
                {t('workflowCompletions:relatedEntities.summaryTable.name')}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant='labelSmall' sx={tableHeadSx}>
                {t('workflowCompletions:relatedEntities.summaryTable.relation')}
              </Typography>
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {relatedEntities?.map((relatedEntity, idx) => (
            <TableRow key={idx} hover>
              <TableCell sx={tableRowSx}>
                <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                  {generateClientNameString(relatedEntity.entity)}
                </Typography>
              </TableCell>
              <TableCell sx={tableRowSx}>
                <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                  {t(`relatedEntityTypes:${relatedEntity.relation}`)}
                </Typography>
              </TableCell>
              <TableCell sx={tableRowSx} padding='checkbox'>
                <IconButton
                  onClick={getHandleDeleteFn(relatedEntity)}
                  aria-label='delete'
                  size='xsmall'
                >
                  <DeleteIcon fontSize='inherit' />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!options.disableAdditionalRelationships && (
        <Box>
          <AddRelationButton />
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <WorkflowCompletionForm
        onSubmit={() => onContinue()}
        submitButtonProps={{
          label: t('client:continue'),
          disabled: loading,
        }}
      >
        <Typography variant='displayLarge' sx={{ mt: 1 }}>
          {translateBackend(options?.title)}
        </Typography>
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackend(options?.subtitle)}
          </ReactMarkdown>
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 3,
          }}
        >
          {loading ? (
            <LoadingAnimation />
          ) : relatedEntities?.length === 0 ? (
            <NoRelatedEntities />
          ) : (
            <RelatedEntitiesTable />
          )}
        </Box>
      </WorkflowCompletionForm>

      <Dialog
        sx={{
          '.MuiDialog-container': {
            '.MuiPaper-root': {
              padding: '20px',
            },
          },
        }}
        open={isModalOpen}
        onClose={handleModalClose}
        fullWidth
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'end',
          }}
        >
          <Box
            onClick={handleModalClose}
            sx={{
              display: 'flex',
              cursor: 'pointer',
            }}
          >
            <Icon icon={CloseRoundedIcon} size='medium' />
          </Box>
        </Box>
        <Form onSubmit={handleSubmit}>
          <Grid
            container
            sx={{
              gap: 4,
            }}
          >
            <Grid size={12}>
              <Typography variant='headingMedium' sx={{ mt: 1 }}>
                {t('workflowCompletions:relatedEntities.formTitle')}
              </Typography>
            </Grid>
            <Grid size={12}>
              {user && (
                <RelatedEntitySelect
                  forUserType={user.type}
                  onEntityChange={(entity) =>
                    setDraftRelatedEntity({ ...draftRelatedEntity, entity })
                  }
                  onRelationChange={(relation) =>
                    setDraftRelatedEntity({ ...draftRelatedEntity, relation })
                  }
                />
              )}
            </Grid>
            <Grid
              size={12}
              sx={{
                justifyContent: 'end',
                display: 'flex',
              }}
            >
              <Button
                type='submit'
                color='primary'
                disabled={isSubmissionDisabled}
              >
                {t('workflowCompletions:relatedEntities.addRelation')}
              </Button>
            </Grid>
          </Grid>
        </Form>
      </Dialog>
      {/* Relation removal confirmation dialog */}
      {deleteConfirmationOpen && deletionCandidate && user && (
        <RelatedEntityRemoveDialog
          user={user}
          existingRelatedEntities={relatedEntities}
          entityToRemove={deletionCandidate.entity}
          onClose={() => setDeleteConfirmationOpen(false)}
        />
      )}
    </>
  );
};

const exampleUser: User = {
  id: 'user',
  subAccounts: [],
  goals: [],
  accounts: [],
  type: EntityTypes.INDIVIDUAL,
  organization: { id: 'org' },
  state: UserStates.ACTIVE,
  physicalAddress: undefined,
  firstName: 'Fred',
  lastName: 'Rogers',
};

export const relatedEntitesVisualPropsExample = {
  user: {
    ...exampleUser,
    relatedEntities: [
      {
        entity: { ...exampleUser, firstName: 'Joanne' },
        relation: RelatedEntityTypes.Spouse,
      },
      {
        entity: {
          ...exampleUser,
          type: EntityTypes.TRUST,
          entityName: "Roger's Trust",
        },
        relation: RelatedEntityTypes.Protector,
      },
    ],
  },
  options: {},
  loading: false,
  onContinue: () => {},
};
